/*
  Mini-tunnel d'abonnement, deux étapes.

  Le tunnel n'applique aucune remise : il ajoute la box et la variante
  d'abonnement au panier, et la remise automatique de la boutique fait le
  −30 %. Si la ligne d'abonnement saute, la remise saute avec elle — c'est
  le garde-fou demandé par le brief, obtenu sans code.
*/
(() => {
  const euro = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  // Un seuil se dit « 50 € », pas « 50,00 € » : les centimes n'apportent rien
  // à une mention et alourdissent la lecture.
  const euroRond = (cents) =>
    cents % 100 === 0
      ? new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
      }).format(cents / 100)
      : euro(cents);

  // « Domicile Colis Privé (peut être déposé…) » → « à domicile (Colis Privé) »
  const phraseLivraison = (mode) => {
    const net = String(mode || '').replace(/\s*\([^)]*\)/g, '').trim();
    if (/^point relais/i.test(net)) return 'en point relais';
    const reste = net.replace(/^domicile\s*/i, '').trim();
    if (!reste || /^standard$/i.test(reste)) return 'à domicile';
    return `à domicile (${reste})`;
  };

  const transporteur = (mode) => String(mode || '').replace(/\s*\([^)]*\)/g, '').trim();

  class Tunnel {
    constructor(racine) {
      this.racine = racine;
      const brut = racine.querySelector('[data-ltb-tunnel-data]');
      this.d = JSON.parse(brut.textContent);

      this.carte = racine.querySelector('.ltb-tunnel__carte');
      this.etapes = {
        0: racine.querySelector('[data-etape="0"]'),
        1: racine.querySelector('[data-etape="1"]'),
        2: racine.querySelector('[data-etape="2"]'),
      };
      this.selectPays = racine.querySelector('[data-tunnel-pays]');
      this.modes = racine.querySelector('[data-tunnel-modes]');
      this.btnSuivant = racine.querySelector('[data-tunnel-suivant]');
      this.btnAjouter = racine.querySelector('[data-tunnel-ajouter]');

      this.formule = 'mensuel';
      this.variante = null;
      this.etape = 1;
      this.rattrapage = 'unique';
      // Vrai quand le tunnel s'ouvre sur l'étape de rattrapage : la box est
      // alors déjà au panier, et il faudra remplacer sa ligne plutôt que
      // d'en ajouter une seconde.

      this.brancher();
    }

    brancher() {
      this.racine.querySelectorAll('[data-tunnel-fermer]').forEach((el) => {
        el.addEventListener('click', () => this.fermer());
      });

      // Cliquer a cote referme : c'est le geste attendu d'une fenetre
      // modale, et le fond sombre invite deja a le faire.
      this.racine.addEventListener('click', (e) => {
        if (!e.target.closest('.ltb-tunnel__carte')) this.fermer();
      });
      // Étape 0 : deux vraies cartes à cocher, comme sur la fiche.
      this.racine.querySelectorAll('[data-rattrapage-input]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.choixRattrapage(input.value);
        });
      });

      this.racine.querySelector('[data-rattrapage-cta]')
        .addEventListener('click', () => {
          if (this.rattrapage !== 'abo') {
            // « Continuer avec ma commande » : la box reste au panier telle
            // quelle, et `fermer` fera ouvrir le tiroir.
            this.fermer();
            return;
          }
          // La fiche derrière doit refléter le choix : si le client referme
          // le tunnel, il retrouve l'offre abonnement sélectionnée.
          document.dispatchEvent(new CustomEvent('ltb:offre-abo', {
            detail: { formule: this.formule },
          }));
          this.montrer(1);
        });
      this.racine.querySelector('[data-tunnel-retour]')
        .addEventListener('click', () => this.montrer(1));
      this.btnSuivant.addEventListener('click', () => {
        if (this.variante) this.montrer(2);
      });
      this.btnAjouter.addEventListener('click', () => this.ajouter());
      this.selectPays.addEventListener('change', () => {
        // Changer de pays invalide le transporteur : ses tarifs ne valent
        // que pour le pays où ils ont été lus.
        this.variante = null;
        this.rendreModes();
        this.majSuivant();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.racine.hidden) this.fermer();
      });
    }

    /* --- Données --- */

    variantes() {
      return (this.d.formules[this.formule]?.variantes || []).filter((v) => v.port !== null);
    }

    base() {
      return this.d.formules[this.formule].base;
    }

    // Une variante peut couvrir plusieurs pays : « Pays-Bas - Allemagne - Espagne ».
    pays() {
      const vus = [];
      this.variantes().forEach((v) => {
        v.pays.split(' - ').map((p) => p.trim()).filter(Boolean).forEach((p) => {
          if (!vus.includes(p)) vus.push(p);
        });
      });
      return vus.sort((a, b) => {
        if (a === 'France') return -1;
        if (b === 'France') return 1;
        return a.localeCompare(b, 'fr');
      });
    }

    variantesDuPays(pays) {
      return this.variantes()
        .filter((v) => v.pays.split(' - ').map((p) => p.trim()).includes(pays))
        .sort((a, b) => a.port - b.port);
    }

    /* --- Rendu --- */

    rendrePays() {
      const liste = this.pays();
      this.selectPays.innerHTML = liste
        .map((p) => `<option value="${p}">${p}</option>`)
        .join('');
      this.selectPays.value = liste[0] || '';
    }

    rendreModes() {
      const choix = this.variantesDuPays(this.selectPays.value);
      this.modes.innerHTML = choix
        .map((v) => `
          <label class="ltb-tunnel__mode" data-variante="${v.id}">
            <span class="ltb-tunnel__mode-corps">
              <span class="ltb-tunnel__mode-nom">${transporteur(v.mode)}</span>
              <span class="ltb-tunnel__mode-detail">${v.delai}</span>
            </span>
            <span class="ltb-tunnel__mode-aside">
              <span class="ltb-tunnel__mode-port">${euro(v.port)}</span>
              <span class="ltb-tunnel__mode-total">soit ${euro(this.base() + v.port)}/mois</span>
            </span>
          </label>`)
        .join('');

      this.modes.querySelectorAll('[data-variante]').forEach((el) => {
        el.addEventListener('click', () => {
          this.variante = choix.find((v) => String(v.id) === el.dataset.variante) || null;
          this.modes.querySelectorAll('[data-variante]').forEach((autre) => {
            autre.classList.toggle('is-selected', autre === el);
          });
          this.majSuivant();
        });
      });
    }

    majSuivant() {
      this.btnSuivant.disabled = !this.variante;
    }

    // « puis dès X €/mois » : la mensualité la moins chère, base plus le port
    // le plus bas de la formule. Même calcul que la carte d'offre de la fiche.
    rendreRattrapage() {
      const cible = this.racine.querySelector('[data-rattrapage-des]');
      const ports = this.variantes().map((v) => v.port);
      if (cible && ports.length) cible.textContent = euro(this.base() + Math.min(...ports));

      const coche = this.racine.querySelector('[data-rattrapage-input][value="unique"]');
      if (coche) coche.checked = true;
      this.choixRattrapage('unique');
    }

    choixRattrapage(valeur) {
      this.rattrapage = valeur === 'abo' ? 'abo' : 'unique';
      const abo = this.rattrapage === 'abo';

      this.racine.querySelectorAll('[data-rattrapage-carte]').forEach((carte) => {
        carte.classList.toggle('is-selected', carte.dataset.rattrapageCarte === this.rattrapage);
      });
      this.racine.querySelector('[data-rattrapage-eco]').hidden = !abo;
      this.racine.querySelector('[data-rattrapage-cta]').textContent = abo
        ? "Je m’abonne et j’en profite"
        : 'Continuer avec ma commande';
    }

    rendreRecap() {
      const v = this.variante;
      const mois = this.d.moisSuivant;
      const box = this.d.box.prixOffre;
      const base = this.base();
      const q = (sel) => this.racine.querySelector(sel);

      // Le seuil de gratuité ne compte que les produits boutique : l'abonnement
      // relève d'un autre profil de livraison et son port n'est jamais offert.
      const grille = this.d.livraisonBoutique || {};
      const boutique = grille[this.selectPays.value] || null;
      const offerte = !!(boutique && boutique.seuil !== null && box >= boutique.seuil);
      const portBox = boutique ? (offerte ? 0 : boutique.port) : null;

      let noteBox = '';
      if (boutique && boutique.seuil !== null) {
        noteBox = offerte
          ? `Offerte : vous dépassez ${euroRond(boutique.seuil)} d’achats boutique`
          : `Offerte dès ${euroRond(boutique.seuil)} d’achats boutique`;
      }

      // Deux colis, deux blocs. L'information d'expédition remonte en tête
      // de groupe : elle vaut pour le produit comme pour sa livraison, et la
      // répéter sur chaque ligne noyait la lecture.
      const groupes = [
        {
          // Une box en précommande part à sa date (`box.expedition`, rendu
          // par Liquid), jamais « sous 24 à 48 h ».
          colis: this.d.box.expedition
            ? `Colis 1 · expédié ${this.d.box.expedition}`
            : 'Colis 1 · expédié sous 24 à 48 h',
          lignes: [
            {
              titre: `La box ${this.d.box.titre} à <strong>−${this.d.remise} %</strong>`,
              prix: euro(box),
            },
            {
              titre: `Livraison · ${boutique ? boutique.mode : 'calculée au paiement'}`,
              note: noteBox,
              prix: portBox === null ? 'au paiement' : (offerte ? 'Offerte' : euro(portBox)),
            },
          ],
        },
        {
          colis: `Colis 2 · expédié entre le 5 et le 10 ${mois}`,
          lignes: [
            {
              titre: `1<sup>re</sup> box de l’abonnement · <strong>${mois}</strong>`,
              prix: euro(base),
            },
            {
              titre: `Livraison · ${transporteur(v.mode)}`,
              note: 'Comprise dans votre mensualité, tous les mois',
              prix: euro(v.port),
            },
          ],
        },
      ];

      q('[data-tunnel-lignes]').innerHTML = groupes
        .map((g) => `
          <div class="ltb-tunnel__groupe">
            <p class="ltb-tunnel__colis">${g.colis}</p>
            ${g.lignes.map((l) => `
              <div class="ltb-tunnel__ligne">
                <div>
                  <p class="ltb-tunnel__ligne-titre">${l.titre}</p>
                  ${l.note ? `<p class="ltb-tunnel__ligne-note">${l.note}</p>` : ''}
                </div>
                <p class="ltb-tunnel__ligne-prix">${l.prix}</p>
              </div>`).join('')}
          </div>`)
        .join('');

      // Sans grille pour ce pays, le port de la box reste inconnu : on annonce
      // un plancher plutôt qu'un total qui serait démenti à la caisse.
      const total = box + base + v.port + (portBox || 0);
      q('[data-tunnel-total]').textContent =
        portBox === null ? `à partir de ${euro(total)}` : euro(total);

      const mensuel = base + v.port;
      q('[data-tunnel-ensuite]').textContent =
        `Ensuite : ${euro(mensuel)}/mois, livraison ${phraseLivraison(v.mode)} incluse.`;
      q('[data-tunnel-conditions]').textContent = this.formule === 'annuel'
        ? "Prélèvement le mois suivant, puis chaque mois. Engagement de 12 mois. Au terme des 12 mois, l’abonnement continue au même tarif, sans engagement, et devient résiliable à tout moment."
        : 'Prélèvement le mois suivant, puis chaque mois. Sans engagement, résiliable à tout moment depuis votre compte.';
    }

    /* --- Navigation --- */

    ouvrir(formule, options = {}) {
      this.formule = formule === 'annuel' ? 'annuel' : 'mensuel';
      this.variante = null;
      this.rendrePays();
      this.rendreModes();
      this.majSuivant();
      this.racine.hidden = false;
      document.body.style.overflow = 'hidden';
      this.montrer(options.etape === 0 ? 0 : 1);
      this.carte.focus?.();
    }

    fermer() {
      const etape = this.etape;
      this.racine.hidden = true;
      document.body.style.overflow = '';
      // Qui a ouvert le tunnel a peut-être une suite à donner — montrer le
      // panier après un rattrapage décliné, par exemple.
      document.dispatchEvent(new CustomEvent('ltb:tunnel-ferme', { detail: { etape } }));
    }

    montrer(n) {
      this.etape = n;
      [0, 1, 2].forEach((i) => { this.etapes[i].hidden = i !== n; });
      if (n === 0) this.rendreRattrapage();
      if (n === 2) this.rendreRecap();
      this.carte.scrollTop = 0;
    }

    async ajouter() {
      if (!this.variante) return;
      const initial = this.btnAjouter.textContent;
      this.btnAjouter.disabled = true;
      this.btnAjouter.textContent = 'Ajout en cours…';
      try {
        // Le client choisit une façon d'acheter CETTE box, pas d'en prendre une
        // seconde. Si elle est déjà au panier — venue du rattrapage, ou ajoutée
        // seule puis complétée par l'abonnement — on n'ajoute que l'abonnement,
        // et la remise de bienvenue se pose sur la ligne existante.
        //
        // On ne retire plus rien. La version précédente vidait la ligne de la
        // box avant de la rajouter : c'était destructeur pour une quantité
        // choisie volontairement, et il fallait la rétablir si l'ajout échouait.
        // Ne rien ajouter est aussi juste, et sans risque.
        let boxDejaLa = false;
        try {
          const lecture = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (lecture.ok) {
            const contenu = await lecture.json();
            boxDejaLa = (contenu.items || []).some((ligne) => String(ligne.id) === String(this.d.box.id));
          }
        } catch (e) {
          // Panier illisible : on ajoute les deux lignes, comme avant.
        }

        const lignes = [];
        if (!boxDejaLa) lignes.push({ id: this.d.box.id, quantity: 1 });
        // Sans son plan Recharge, la ligne part en achat unique : le client
        // paie « Abonnement » et aucun abonnement n'est créé (mesuré le 21/09 :
        // quatre commandes du 16 au 19/09). Plutôt refuser que vendre ça.
        if (!this.variante.plan) throw new Error('Cette formule est momentanément indisponible. Choisissez-la depuis la page des abonnements.');
        lignes.push({ id: this.variante.id, quantity: 1, selling_plan: this.variante.plan });

        const reponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lignes }),
        });
        if (!reponse.ok) throw new Error(await reponse.text());
        window.location.href = '/cart';
      } catch (erreur) {
        this.btnAjouter.disabled = false;
        this.btnAjouter.textContent = initial;
        console.error('[ltb-tunnel]', erreur);
      }
    }
  }

  const demarrer = () => {
    const racine = document.querySelector('[data-ltb-tunnel]');
    if (!racine || racine.dataset.pret) return;
    racine.dataset.pret = '1';
    // `position: fixed` perd son ancrage a la fenetre sous un ancetre
    // transforme, et le theme en pose au fil des animations. Remonter
    // l'element au body supprime le probleme a la racine.
    if (racine.parentElement !== document.body) document.body.appendChild(racine);
    const tunnel = new Tunnel(racine);
    document.addEventListener('ltb:tunnel', (e) => tunnel.ouvrir(e.detail?.formule, e.detail || {}));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
